import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import cx from "classnames";

import styles from "./Marketplace.module.css";
import { money } from "../../utils/format";
import { marketCurrency } from "../../utils/market";
import { track, EVENTS } from "../../utils/analyticsService";
import { shouldShowStrip, isPick, countByKind } from "../../utils/bestsellers";
import { subscriptionsFirst } from "./heroBrands";

// Rows in the hero panel.
export const PANEL_MAX_ITEMS = 6;

const PanelLogo = ({ src, name }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className={cx(styles.panelLogo, styles.logoFallback)} aria-hidden="true">
        {String(name || "?").charAt(0).toUpperCase()}
      </span>
    );
  }
  return <img className={styles.panelLogo} src={src} alt="" width="40" height="40" loading="lazy" onError={() => setFailed(true)} />;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * What the hero panel shows: the store's bestsellers (ranked by what actually
 * sold, then any Fleek picks), or - where nothing has sold yet, as in a new
 * country's store - its biggest discounts, subscriptions first, titled "Top
 * deals". Never calls
 * anything a bestseller that isn't one. Null when there's too little to show.
 */
export const panelFor = ({ bestsellers = [], brands = [] }) => {
  const byPath = new Map(brands.filter((b) => b?.vendor_web_path).map((b) => [b.vendor_web_path, b]));
  if (shouldShowStrip(bestsellers)) {
    const currency = marketCurrency();
    return {
      kind: "bestsellers",
      title: "Bestsellers",
      sub: "What buyers on Fleek pick most, over the last 90 days.",
      items: bestsellers.slice(0, PANEL_MAX_ITEMS).map((item) => {
        const brand = byPath.get(item.vendorWebPath);
        return {
          key: item.vendorId,
          path: item.vendorWebPath,
          name: item.name,
          logo: item.logoUrl,
          category: item.category || brand?.service?.category || "",
          price: item.startsFrom != null ? `${money(item.startsFrom, currency)}${item.period ? `/${item.period}` : ""}` : brand?.starts_from || "",
          mrp: num(brand?.mrp),
          discount: num(brand?.discount_percentage),
          source: item,
        };
      }),
    };
  }
  // Subscriptions first: gift cards and games only fill a store that has too
  // few other discounted brands (./heroBrands.js).
  const deals = subscriptionsFirst(
    brands
      .filter((b) => b?.vendor_web_path && num(b.discount_percentage) > 0)
      .sort((a, b) => num(b.discount_percentage) - num(a.discount_percentage)),
    PANEL_MAX_ITEMS
  );
  if (deals.length < 3) return null;
  return {
    kind: "deals",
    title: "Top deals",
    sub: "The biggest discounts in this store right now.",
    items: deals.map((b, i) => ({
      key: b.vendor_id ?? b.vendor_web_path,
      path: b.vendor_web_path,
      name: b.service?.service_name || "",
      logo: b.service?.logo_url,
      category: b.service?.category || "",
      price: b.starts_from || "",
      mrp: num(b.mrp),
      discount: num(b.discount_percentage),
      position: i + 1,
    })),
  };
};

const Chip = ({ item }) => {
  if (!item.source) return null;
  return isPick(item.source) ? (
    <span className={cx(styles.bestRank, styles.bestPick)}>Fleek pick</span>
  ) : (
    <span className={styles.bestRank} aria-label={`Number ${item.source.bestsellerRank ?? item.source.rank}`}>
      {item.source.bestsellerRank ?? item.source.rank}
    </span>
  );
};

/**
 * The marketplace hero's right-hand panel (replaces "Deal of the day" and its
 * daily countdown, which was never a real deadline).
 */
export const BestsellersPanel = ({ panel, footer }) => {
  const viewed = useRef(false);
  const shown = panel?.items || [];
  const sources = shown.map((i) => i.source).filter(Boolean);
  const { bestsellers: bestsellerCount, picks: pickCount } = countByKind(sources);

  useEffect(() => {
    if (!panel || viewed.current) return;
    viewed.current = true;
    if (panel.kind === "bestsellers") {
      track(EVENTS.BESTSELLERS_VIEWED, { count: shown.length, bestseller_count: bestsellerCount, pick_count: pickCount });
    } else {
      track(EVENTS.TOP_DEALS_VIEWED, { count: shown.length });
    }
  }, [panel, shown.length, bestsellerCount, pickCount]);

  if (!panel) return null;

  const onClick = (item) => {
    if (item.source) {
      track(EVENTS.BESTSELLER_CLICKED, { rank: item.source.rank, kind: item.source.kind, vendor_id: item.source.vendorId, where: "panel" });
    } else {
      track(EVENTS.TOP_DEAL_CLICKED, { position: item.position, vendor_id: item.key });
    }
  };

  return (
    <nav className={styles.panel} aria-label={panel.title}>
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>{panel.title}</span>
        <span className={styles.panelSub}>{panel.sub}</span>
      </div>
      <ol className={styles.panelList}>
        {shown.map((item) => (
          <li key={item.key} className={styles.panelListItem}>
            <Link to={`/brand/${item.path}`} className={styles.panelRow} onClick={() => onClick(item)}>
              <Chip item={item} />
              <PanelLogo src={item.logo} name={item.name} />
              <span className={styles.panelText}>
                <span className={styles.panelName}>{item.name}</span>
                {item.category && <span className={styles.panelMeta}>{item.category}</span>}
              </span>
              <span className={styles.panelPrices}>
                {item.price && <span className={styles.panelPrice}>from {item.price}</span>}
                {item.discount > 0 && <span className={styles.panelBadge}>UPTO {Math.round(item.discount)}% off</span>}
              </span>
            </Link>
          </li>
        ))}
      </ol>
      {footer && <div className={styles.panelFoot}>{footer}</div>}
    </nav>
  );
};

export default BestsellersPanel;
